import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, IconButton, InputAdornment, Modal, Stack, TextField, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { REQUIRED_RED } from '../../../App';
import { MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';
import { handlePreventKeyDownDecimal } from '../../../utility/helpers/commonFunction';
import { formatTaxPercent, taxPercentInputRegex } from '../../../utility/helpers/taxCalculation';
import { useTaxDecimalPrecision } from '../../../utility/hooks/useTaxDecimalPrecision';

const MODAL_STYLE = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: '#FFF',
    borderRadius: '12px',
    boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
    overflow: 'hidden',
    width: '560px',
    maxWidth: 'calc(100vw - 24px)',
};

const LABEL_STYLE = {
    color: 'rgba(0,0,0,0.87)',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    mb: '8px',
};

const DEFAULT_VALUES = {
    taxName: '',
    taxCode: '',
    taxRate: '',
    appliesToParts: true,
    appliesToLabour: true,
    appliesToServicePackage: true,
    appliesToFees: true,
};

const SalesTaxModal = ({ open, onClose, onSave, updateIndex, setUpdateIndex, taxRules = [] }) => {
    const [nameError, setNameError] = useState('');
    const isEditMode = updateIndex !== -1;
    const precision = useTaxDecimalPrecision();

    const [isTaxCodeEdited, setIsTaxCodeEdited] = useState(false);

    const {
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: DEFAULT_VALUES,
    });

    const appliesToParts = watch('appliesToParts');
    const appliesToLabour = watch('appliesToLabour');
    const appliesToServicePackage = watch('appliesToServicePackage');
    const appliesToFees = watch('appliesToFees');

    useEffect(() => {
        if (isEditMode && taxRules?.[updateIndex]) {
            const currentRule = taxRules[updateIndex];
            // Existing record: load the saved taxCode exactly as stored. isTaxCodeEdited stays
            // false so that if the user retypes the Name field, the code still auto-recalculates
            // (per requirement) — it just won't be touched by this programmatic load itself,
            // since generation now only runs from the Name field's own onChange (real keystrokes).
            setIsTaxCodeEdited(false);
            setValue('taxName', currentRule?.taxName || '');
            setValue('taxCode', currentRule?.taxCode || '');
            // One rate drives all three documents; older rules stored one per document type.
            const existingRate = currentRule?.taxRate ?? currentRule?.invoiceRate ?? currentRule?.estimateRate ?? currentRule?.workOrderAuthorizedRate;
            setValue('taxRate', existingRate != null ? formatTaxPercent(existingRate, precision) : '');
            setValue('appliesToParts', currentRule?.appliesToParts ?? true);
            setValue('appliesToLabour', currentRule?.appliesToLabour ?? true);
            setValue('appliesToServicePackage', currentRule?.appliesToServicePackage ?? true);
            setValue('appliesToFees', currentRule?.appliesToFees ?? true);
        } else if (open) {
            reset(DEFAULT_VALUES);
            setIsTaxCodeEdited(false);
        }
    }, [isEditMode, open, reset, setValue, taxRules, updateIndex, precision]);

    const generateTaxCode = (name = '') => {
        if (!name || typeof name !== 'string') return '';
        const value = name.trim();
        // Already a short code like GST, VAT, GSTA, GSTAF
        if (/^[A-Za-z]{1,5}$/.test(value)) {
            return value.toUpperCase();
        }
        const words = value
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .split(/\s+/)
            .filter(Boolean);

        return words
            .map((word) => word.charAt(0).toUpperCase())
            .join('')
            .slice(0, 5);
    };

    const handleClose = useCallback(() => {
        setUpdateIndex(-1);
        reset(DEFAULT_VALUES);
        setNameError('');
        onClose(false);
    }, [onClose, reset, setUpdateIndex]);

    const handleFormSubmit = useCallback(
        (values) => {
            setNameError('');

            const duplicateRule = taxRules.find(
                (rule, index) => rule.taxName.trim().toLowerCase() === values.taxName.trim().toLowerCase() && index !== updateIndex
            );

            if (duplicateRule) {
                setNameError('Tax label already exists. Please choose a different name.');
                return;
            }

            const payload = {
                taxName: values.taxName.trim(),
                taxCode: values.taxCode.trim().toUpperCase(),
                // The same rate is written to every document type's key (see SalesTax.jsx).
                taxRate: parseFloat(values.taxRate),
                invoiceRate: parseFloat(values.taxRate),
                estimateRate: parseFloat(values.taxRate),
                workOrderAuthorizedRate: parseFloat(values.taxRate),
                appliesToParts: values.appliesToParts,
                appliesToLabour: values.appliesToLabour,
                appliesToServicePackage: values.appliesToServicePackage,
                appliesToFees: values.appliesToFees,
            };

            let updatedRules = [];

            if (isEditMode) {
                updatedRules = [...taxRules];
                updatedRules[updateIndex] = {
                    ...updatedRules[updateIndex],
                    ...payload,
                };
            } else {
                updatedRules = [...taxRules, payload];
            }

            onSave(updatedRules);
            handleClose();
        },
        [handleClose, isEditMode, onSave, taxRules, updateIndex]
    );

    return (
        <Modal
            open={open}
            onClose={(event, reason) => {
                if (reason && reason === 'backdropClick') {
                    return;
                }
                handleClose();
            }}
        >
            <Box sx={MODAL_STYLE}>
                <Box
                    sx={{
                        height: '64px',
                        px: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid rgba(0,0,0,0.10) !important',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '20px',
                            fontWeight: 500,
                            color: '#1F1F1F',
                            lineHeight: '32px',
                        }}
                    >
                        {isEditMode ? 'Edit Additional Sales Tax' : 'Add Additional Sales Tax'}
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            width: 36,
                            height: 36,
                            mr: '-8px',
                            color: '#1F1F1F',
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 30 }} />
                    </IconButton>
                </Box>

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Box sx={{ px: '24px', pt: '24px', pb: '24px' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography sx={LABEL_STYLE}>Label Name</Typography>

                                <Controller
                                    name="taxName"
                                    control={control}
                                    rules={{
                                        minLength: {
                                            value: 1,
                                            message: 'Label name is required',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            onChange={(e) => {
                                                const newName = e.target.value;
                                                field.onChange(newName);
                                                // Only auto-derive the code while the user hasn't
                                                // manually typed their own code. This fires on real
                                                // keystrokes only, so an edit-mode prefill (set via
                                                // setValue) never gets overwritten.
                                                if (!isTaxCodeEdited) {
                                                    setValue('taxCode', generateTaxCode(newName), {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                }
                                            }}
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Sales Tax"
                                            error={!!errors.taxName || !!nameError}
                                            helperText={errors.taxName ? errors.taxName.message : nameError}
                                            sx={MuiTextFieldWithFocus}
                                            inputProps={{ maxLength: 50 }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={LABEL_STYLE}>Short form (5 letters)</Typography>

                                <Controller
                                    name="taxCode"
                                    control={control}
                                    rules={{
                                        required: 'Short form is required',
                                        maxLength: {
                                            value: 5,
                                            message: 'Maximum 5 characters',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder="Sales Tax"
                                            error={!!errors.taxCode}
                                            helperText={errors.taxCode?.message}
                                            sx={MuiTextFieldWithFocus}
                                            inputProps={{
                                                maxLength: 5,
                                            }}
                                            onChange={(e) => {
                                                setIsTaxCodeEdited(true);
                                                field.onChange(e.target.value);
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography sx={LABEL_STYLE}>Tax Rate</Typography>

                                <Controller
                                    name="taxRate"
                                    control={control}
                                    rules={{
                                        validate: (value) => {
                                            const num = parseFloat(value);
                                            if (isNaN(num) || num <= 0) return 'Tax rate must be greater than 0';
                                            if (num > 100) return 'Tax rate cannot exceed 100';
                                            return true;
                                        },
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === '' || taxPercentInputRegex(precision).test(v)) field.onChange(v);
                                            }}
                                            fullWidth
                                            variant="outlined"
                                            placeholder="0.00"
                                            error={!!errors.taxRate}
                                            helperText={errors.taxRate?.message || ''}
                                            onKeyDown={(e) => handlePreventKeyDownDecimal(e, precision)}
                                            sx={MuiTextFieldWithFocus}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                            }}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography sx={{ ...LABEL_STYLE, mb: 1 }}>Apply tax on:</Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        columnGap: '28px',
                                        rowGap: '10px',
                                    }}
                                >
                                    <Controller
                                        name="appliesToParts"
                                        control={control}
                                        render={({ field }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value}
                                                    size="small"
                                                    sx={{
                                                        p: 0,
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '16px',
                                                        color: '#1F1F1F',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Parts
                                                </Typography>
                                            </Box>
                                        )}
                                    />

                                    <Controller
                                        name="appliesToLabour"
                                        control={control}
                                        render={({ field }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value}
                                                    size="small"
                                                    sx={{
                                                        p: 0,
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '16px',
                                                        color: '#1F1F1F',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Labour
                                                </Typography>
                                            </Box>
                                        )}
                                    />

                                    <Controller
                                        name="appliesToServicePackage"
                                        control={control}
                                        render={({ field }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value}
                                                    size="small"
                                                    sx={{
                                                        p: 0,
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '16px',
                                                        color: '#1F1F1F',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Fixed Price
                                                </Typography>
                                            </Box>
                                        )}
                                    />

                                    <Controller
                                        name="appliesToFees"
                                        control={control}
                                        render={({ field }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value}
                                                    size="small"
                                                    sx={{
                                                        p: 0,
                                                        mr: 1,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '16px',
                                                        color: '#1F1F1F',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Fees
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(0,0,0,0.12)' }} />

                    <Box sx={{ px: '16px', py: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" color="primary" onClick={handleClose} sx={{ minWidth: 100, height: 42 }}>
                                CANCEL
                            </Button>

                            <Button variant="contained" color="primary" type="submit" sx={{ minWidth: 82, height: 42 }}>
                                SAVE
                            </Button>
                        </Stack>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};

export default memo(SalesTaxModal);
